/**
 * Custom hook for OKR data fetching
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { okrService } from '../services';
import type {
  CreateOKRRequest,
  UpdateOKRRequest,
  OKRFilters,
  AddKeyResultRequest,
  UpdateProgressRequest
} from '../types/okr';

/**
 * Hook to fetch OKRs with filters
 *
 * `options.enabled` (default true, same as react-query) lets a caller hold off issuing the
 * request — e.g. the OKR list while a legacy `?departmentIds=<name>` link is still being
 * resolved against filter metadata (`OKRList.tsx`). `enabled: undefined` behaves as `true`.
 */
export const useOKRs = (filters?: OKRFilters, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['okrs', filters],
    queryFn: () => okrService.getOKRs(filters),
    enabled: options?.enabled
  });
};

/**
 * Hook to fetch every OKR matching the filters, across all pages (see okrService.getAllOKRs)
 */
export const useAllOKRs = (filters?: Omit<OKRFilters, 'page' | 'limit'>, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['okrs', 'all', filters],
    queryFn: () => okrService.getAllOKRs(filters),
    enabled: options?.enabled
  });
};

/**
 * Hook to fetch a single OKR by ID
 */
export const useOKR = (id: string) => {
  return useQuery({
    queryKey: ['okr', id],
    queryFn: () => okrService.getOKRById(id),
    enabled: !!id
  });
};

/**
 * Hook to create a new OKR
 */
export const useCreateOKR = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateOKRRequest) => okrService.createOKR(data),
    onSuccess: () => {
      // Invalidate and refetch OKRs list
      queryClient.invalidateQueries({ queryKey: ['okrs'] });
      // Invalidate org stats (dashboard cards)
      queryClient.invalidateQueries({ queryKey: ['org-stats'] });
      // Invalidate sidebar progress and reports
      queryClient.invalidateQueries({ queryKey: ['sidebar-progress'] });
      queryClient.invalidateQueries({ queryKey: ['reports'] });
    }
  });
};

/**
 * Hook to update an OKR
 */
export const useUpdateOKR = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateOKRRequest }) =>
      okrService.updateOKR(id, data),
    onSuccess: (_, variables) => {
      // Invalidate specific OKR and list
      queryClient.invalidateQueries({ queryKey: ['okr', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['okrs'] });
      // Invalidate org stats (dashboard cards - status changes affect active count)
      queryClient.invalidateQueries({ queryKey: ['org-stats'] });
      // Invalidate sidebar progress and reports
      queryClient.invalidateQueries({ queryKey: ['sidebar-progress'] });
      queryClient.invalidateQueries({ queryKey: ['reports'] });
    }
  });
};

/**
 * Hook to close an OKR (set to closed)
 */
export const useCloseOKR = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => okrService.closeOKR(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['okrs'] });
      queryClient.invalidateQueries({ queryKey: ['org-stats'] });
      queryClient.invalidateQueries({ queryKey: ['sidebar-progress'] });
      queryClient.invalidateQueries({ queryKey: ['reports'] });
    }
  });
};

/**
 * Hook to delete an OKR
 */
export const useDeleteOKR = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => okrService.deleteOKR(id),
    onSuccess: () => {
      // Invalidate OKRs list
      queryClient.invalidateQueries({ queryKey: ['okrs'] });
      // Invalidate org stats (dashboard cards)
      queryClient.invalidateQueries({ queryKey: ['org-stats'] });
      // Invalidate sidebar progress and reports
      queryClient.invalidateQueries({ queryKey: ['sidebar-progress'] });
      queryClient.invalidateQueries({ queryKey: ['reports'] });
    }
  });
};

/**
 * Hook to duplicate an OKR
 */
export const useDuplicateOKR = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => okrService.duplicateOKR(id),
    onSuccess: () => {
      // Invalidate OKRs list to show the new duplicated OKR
      queryClient.invalidateQueries({ queryKey: ['okrs'] });
      // Invalidate org stats (dashboard cards)
      queryClient.invalidateQueries({ queryKey: ['org-stats'] });
      // Invalidate sidebar progress and reports
      queryClient.invalidateQueries({ queryKey: ['sidebar-progress'] });
      queryClient.invalidateQueries({ queryKey: ['reports'] });
    }
  });
};

/**
 * Hook to add a key result to an OKR
 */
export const useAddKeyResult = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ okrId, data }: { okrId: string; data: AddKeyResultRequest }) =>
      okrService.addKeyResult(okrId, data),
    onSuccess: (_, variables) => {
      // Invalidate specific OKR
      queryClient.invalidateQueries({ queryKey: ['okr', variables.okrId] });
    }
  });
};

/**
 * Hook to update key result progress
 */
export const useUpdateProgress = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ keyResultId, data }: { keyResultId: string; data: UpdateProgressRequest }) =>
      okrService.updateProgress(keyResultId, data),
    onSuccess: (keyResult) => {
      // Invalidate the OKR that contains this key result
      queryClient.invalidateQueries({ queryKey: ['okr', keyResult.okrId] });
      queryClient.invalidateQueries({ queryKey: ['okrs'] });
      // Invalidate org stats (dashboard cards - progress updates affect averages)
      queryClient.invalidateQueries({ queryKey: ['org-stats'] });
      // Invalidate sidebar progress and reports (progress updates affect averages)
      queryClient.invalidateQueries({ queryKey: ['sidebar-progress'] });
      queryClient.invalidateQueries({ queryKey: ['reports'] });
    }
  });
};

/**
 * Hook to update key result details
 */
export const useUpdateKeyResult = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ keyResultId, data }: { keyResultId: string; data: Partial<AddKeyResultRequest> }) =>
      okrService.updateKeyResult(keyResultId, data),
    onSuccess: (keyResult) => {
      // Invalidate the OKR that contains this key result
      queryClient.invalidateQueries({ queryKey: ['okr', keyResult.okrId] });
      queryClient.invalidateQueries({ queryKey: ['okrs'] });
      // Invalidate sidebar progress and reports
      queryClient.invalidateQueries({ queryKey: ['sidebar-progress'] });
      queryClient.invalidateQueries({ queryKey: ['reports'] });
    }
  });
};

/**
 * Hook to delete a key result
 */
export const useDeleteKeyResult = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (keyResultId: string) => okrService.deleteKeyResult(keyResultId),
    onSuccess: () => {
      // Invalidate all OKRs since we don't know which OKR contained this KR
      queryClient.invalidateQueries({ queryKey: ['okrs'] });
      queryClient.invalidateQueries({ queryKey: ['okr'] });
      // Invalidate sidebar progress and reports
      queryClient.invalidateQueries({ queryKey: ['sidebar-progress'] });
      queryClient.invalidateQueries({ queryKey: ['reports'] });
    }
  });
};

/**
 * Hook to duplicate a key result to a target OKR
 */
export const useDuplicateKeyResult = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ keyResultId, targetOkrId }: { keyResultId: string; targetOkrId: string }) =>
      okrService.duplicateKeyResult(keyResultId, targetOkrId),
    onSuccess: () => {
      // Invalidate all OKRs to show the duplicated KR in target OKR
      queryClient.invalidateQueries({ queryKey: ['okrs'] });
      queryClient.invalidateQueries({ queryKey: ['okr'] });
      // Invalidate sidebar progress and reports
      queryClient.invalidateQueries({ queryKey: ['sidebar-progress'] });
      queryClient.invalidateQueries({ queryKey: ['reports'] });
    }
  });
};

/**
 * Hook to fetch available parent OKRs for alignment
 */
export const useAvailableParentOKRs = (
  okrType: 'company' | 'department' | 'team' | 'individual',
  excludeId?: string
) => {
  return useQuery({
    queryKey: ['available-parents', okrType, excludeId],
    queryFn: () => okrService.getAvailableParentOKRs(okrType, excludeId),
    enabled: !!okrType
  });
};
